<template>
  <InputWrapper
    v-if="options"
    v-model="model"
    :className="`input-row--select ${className}`"
    :name="name"
    :error="error"
    :label="label"
    :helperText="helperText"
    :placeholder="placeholder"
    :disabled="disabled ?? false"
    :showRequired="showRequired"
    :required="required"
  >
    <template #icon>
      <slot name="icon"></slot>
    </template>

    <template #input>
      <div ref="input-select">
        <div
          :name="name"
          class="form-input form-input--select"
          @click="toggleExpansion"
          tabindex="0"
          @keydown.enter="onEnterPressed"
          :data-testid="dataTestid"
        >
          <slot name="displayText">
            <template v-if="capsuleStyle">
              <div class="chips-container">
                <Chip
                  v-for="option in optionObjects"
                  :key="getOptionValue(option)"
                  :id="getOptionId(option)"
                  :colored="true"
                  :canDelete="true"
                  @click="onChipClick(option)"
                >
                  {{ getOptionLabel(option) }}
                </Chip>
              </div>
            </template>
            <template v-else>
              {{ displayText }}
            </template>
          </slot>
          <IconCaretDown
            class="form-input--select-arrow"
            :class="{ 'form-input--select-arrow-up': isExpanded }"
          />
        </div>

        <Teleport to="#select-anchor">
          <div
            class="form-input--select-dropdown-container"
            ref="options-container"
            tabindex="0"
            v-show="isExpanded"
          >
            <ul
              tabindex="0"
              ref="select-options"
              role="listbox"
              class="form-input--select-dropdown"
              :style="calculatedStyle"
              @keydown.tab="updateTab"
              @keydown.up="arrowUp"
              @keydown.down="arrowDown"
              @keydown.escape="onEscapePressed"
            >
              <div data-quick-filters>
                <slot name="quick-filters" />
              </div>
              <li v-show="filter" tabindex="0" class="form-input--select-filter">
                <InputCheckbox v-if="canSelectAll" :checked="allChecked" @click="selectAll" />
                <InputText
                  v-model="search"
                  ref="input-filter"
                  name="filter"
                  :hasIconRight="true"
                  :placeholder="filterPlaceholder"
                >
                  <template #iconRight>
                    <IconSearch />
                  </template>
                </InputText>
              </li>
              <li v-if="!availableOptions || availableOptions?.length === 0" class="form-input--select-none">
                <span v-html="noOptionMessage"></span>
              </li>
              <li
                v-if="allowClear"
                tabindex="0"
                @click="model = null"
                class="form-input--select-clear"
              >
                <IconDelete />
                Clear Selection
              </li>
              <RecycleScroller
                v-if="availableOptions"
                :items="availableOptions"
                class="scroller"
                :style="recycleScrollerStyle"
                :item-size="32"
                :key-field="keyField"
                v-slot="{ item }"
              >
                <li
                  role="option"
                  tabindex="0"
                  :key="getOptionValue(item)"
                  @keydown.enter="selectOption(getOptionValue(item))"
                  @click="selectOption(getOptionValue(item))"
                  :class="{
                    'form-input--option-active': getOptionValue(item) === model,
                    'form-input--select-option--disabled':
                      maxSelections &&
                      maxSelections === model?.length &&
                      !model.includes(getOptionValue(item)),
                  }"
                >
                  <slot name="optionText" v-bind="item">
                    <InputCheckbox v-if="multiple" :checked="model?.includes(getOptionValue(item))"  />
                    <div>
                      {{ getOptionLabel(item) }}
                      <span
                        v-if="getOptionSubtitle(item)"
                        class="form-input--select-option-subtitle"
                      >
                        {{ getOptionSubtitle(item) }}
                      </span>
                    </div>
                  </slot>
                </li>
              </RecycleScroller>
              <div data-footer>
                <slot name="footer" />
              </div>
            </ul>
          </div>
        </Teleport>
      </div>
    </template>
  </InputWrapper>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef } from 'vue';
import { createFocusTrap, type FocusTrap } from 'focus-trap';
import { RecycleScroller } from 'vue-virtual-scroller';

import Chip from '../global/Chip.vue';
import InputCheckbox from './InputCheckbox.vue';
import InputText from './InputText.vue';
import InputWrapper from './InputWrapper.vue';

import IconCaretDown from '@/icons/arrows/IconCaretDown.svg?component';
import IconSearch from '@/icons/IconSearch.svg?component';
import IconDelete from '@/icons/IconDelete.svg?component';

const model = defineModel<any | any[]>();
const emit = defineEmits(['optionSelected', 'closedDropdown']);

const inputSelect = useTemplateRef('input-select');
const inputFilter = useTemplateRef('input-filter');
const optionsContainer = useTemplateRef('options-container');
const selectOptions = useTemplateRef('select-options');

const calculatedStyle = ref({});
const recycleScrollerStyle = ref({});

const search = ref('');

let trap: FocusTrap | undefined = undefined;

type optionType = {
  value?: string | number;
  id?: string | number | null;
  label?: string;
  display_name?: string;
  sso_enabled?: boolean;
};

const props = withDefaults(
  defineProps<{
    // Input Basics

    /**
     *  A label is required for text fields and succinctly describes the field’s purpose. If another text  element is acting as the label, like a section title, then the label can be visually hidden.
     */
    label?: string;

    /**
     *  Helper text is placed below the field and informs the user of any  requirements or disclaimers. Helper text can be shown persistently or on focus (active state). Helper text will be replaced with an error message if an error occurs.
     */
    helperText?: string;

    /**
     *  Text for the the error if one exists. This overwrites Helper Text
     */
    error?: string;

    /**
     * Whether the field is editable or read-only
     */
    disabled?: boolean | null;

    /**
     *  An asterisk will display next to the label to indicate a field is required. The word “optional” in parentheses will appear next to the field label if the field isn’t required. If most fields are required, only indicate the optional fields. If most fields are optional, only indicate the required fields.
     */
    required?: boolean;

    /**
     *  You can hide the * or (optional) tags for one-off inputs like search fields.
     */

    showRequired?: boolean;

    /**
     *  Placeholder text is optional and remains visible until one or more characters are entered. Because the placeholder text disappears, putting instructions or requirements as placeholder text is not accessible. Helper text is the preferred  method to convey this information.
     */
    placeholder?: string;

    /**
     * Populates both the name and id (also ties the label to the input). Required for accessibility.
     */
    name?: string;

    // Select Basics

    /**
     * Whether there is an X button to allow clearing the input easily.
     */
    allowClear?: boolean;

    /**
     * Whether the Dropdown should include a Search input.
     */
    filter?: boolean;

    /**
     * If there is a Search input, what its placeholder text is.
     */
    filterPlaceholder?: string;

    /**
     * String for a custom class name.
     */
    className?: string;

    /**
     * String for a test-id for testing purposes.
     */
    dataTestid?: string;

    /**
     * The list of possible dropdown options to choose from.
     */
    options: optionType[] | string[] | any[] | null;

    /**
     * The property name of the option that should be used for the label. Typically something like "title", "name", or "label".
     */
    optionLabel?: string;

    /**
     * The property name of the option that should be used for the value. Typically something like "id" or "value"
     */
    optionValue?: string;

    // Mutlselect Specifics

    /**
     * The property name of the option that should be used for subtext. We use this when there's extra data needed. Eg if the label is the Event Name, this might be the Event Date. Or it could be an Account name, etc.
     */
    optionSubtitle?: string;

    /**
     * A function for if the subtitle name needs special formatting (eg: dates)
     */
    optionSubtitleFormatter?: (subtitle: string) => string;

    /**
     * The property name of the option that should be used for subtext. We use this when there's extra data needed. Eg if the label is the Event Name, this might be the Event Date. Or it could be an Account name, etc.
     */
    noOptionMessage?: string;

    /**
     * Whether the Select is actually a Multiselect
     */
    multiple?: boolean;

    /**
     * If it is a Multiselect, what's the max number of items you can select?
     */
    maxSelections?: number;

    /**
     * If it is a Multiselect, should there be a quick use selectAll button?
     */
    canSelectAll?: boolean;

    /**
     * If it is a Multiselect, do we display the chosen results as Chips / Capsules?
     */
    capsuleStyle?: boolean;
  }>(),
  {
    allowClear: false,
    label: '',
    disabled: null,
    name: '',
    error: '',
    placeholder: '',
    options: null,
    filter: false,
    className: '',
    hasIconLeft: false,
    dataTestid: '',
    showRequired: true,
    required: false,
    noOptionMessage: 'No available options',

    multiple: false,
    filterPlaceholder: '',
    canSelectAll: false,
    capsuleStyle: false,
  },
);

const isExpanded = ref(false);

const outsideClickListener = (event: Event) => {
  if (!inputSelect.value?.contains(event.target as HTMLInputElement)) {
    if (isExpanded.value) {
      if (!optionsContainer.value?.contains(event.target as HTMLInputElement)) {
        closeDropdown();
      }
    }
  }
};

const openDropdown = () => {
  isExpanded.value = true;
  document.addEventListener('click', outsideClickListener, true);
  document.addEventListener('scroll', calculateSizePosition, true);

  nextTick(() => {
    // Calculate size and position after elements are rendered
    calculateSizePosition();

    // Set up Focus Trap after elements are visible because otherwise it throws fits.
    const inputDiv = optionsContainer.value as HTMLElement;
    trap = createFocusTrap(inputDiv, { clickOutsideDeactivates: true });
    trap.activate();
    highlightIndex.value = -1;

    // If we are using Filter, autofocus the input when we open
    // the dropdown menu.
    if (props.filter) {
      if (inputFilter.value?.inputText) {
        inputFilter.value.inputText.focus();
      }
    }
  });
};

const closeDropdown = () => {
  isExpanded.value = false;
  document.removeEventListener('click', outsideClickListener, true);
  document.removeEventListener('scroll', calculateSizePosition, true);
  trap?.deactivate();
  emit('closedDropdown');
};

const toggleExpansion = () => {
  if (!isExpanded.value && !props.disabled) {
    openDropdown();
  } else {
    closeDropdown();
  }
};

const onEnterPressed = (e: Event) => {
  const target = e.target as HTMLElement;
  if (!isExpanded.value && target.tagName != 'LI') {
    openDropdown();
  } else {
    closeDropdown();
  }
};

const onEscapePressed = () => {
  closeDropdown();
};

const prevIndex = () => {
  if (highlightIndex.value <= 0) {
    highlightIndex.value = (props.options?.length || 0) - 1;
  } else {
    highlightIndex.value = highlightIndex.value - 1;
  }
};

const arrowUp = (e: KeyboardEvent) => {
  e.preventDefault();
  prevIndex();
  if (listItems.value) {
    listItems.value[highlightIndex.value].focus();
  }
};

const nextIndex = () => {
  if (highlightIndex.value === (props.options?.length || 0) - 1) {
    highlightIndex.value = 0;
  } else {
    highlightIndex.value = highlightIndex.value + 1;
  }
};

const arrowDown = (e: KeyboardEvent) => {
  e.preventDefault();
  nextIndex();
  if (listItems.value) {
    listItems.value[highlightIndex.value].focus();
  }
};

const updateTab = (e: KeyboardEvent) => {
  if (e.shiftKey) {
    prevIndex();
  } else {
    nextIndex();
  }
};

const keyField = computed(() => {
  if (props.options?.length) {
    if (props.optionValue) {
      return props.optionValue;
    }

    if (props.options[0]?.id !== undefined) {
      return 'id';
    } else if (props.options[0].name !== undefined) {
      return 'name';
    } else {
      return 'value';
    }
  }
  return 'id';
});

const optionObjects = computed(() => {
  if (props.multiple && model.value?.length) {
    return props.options?.filter((opt) => model.value?.includes(getOptionValue(opt)));
  }
  return [];
});

const onChipClick = (option: optionType | string) => {
  removeOption(option);
};

const displayText = computed(() => {
  if (props.multiple && model.value?.length) {
    const optionLabels = model.value.map((option: any) => getOptionLabel(option));
    return optionLabels.join(', ');
  } else if (model.value) {
    return getOptionLabel(model.value);
  } else if (props.placeholder) {
    return props.placeholder;
  }
  return '';
});

const getOptionValue = (option: any) => {
  if (props.optionValue) {
    return option[props.optionValue];
  } else if (typeof option === 'object') {
    if (props.multiple) {
      return option;
    }

    if (option.id) {
      return option.id;
    }
    if (option.value) {
      return option.value;
    }
  }
  return option;
};

const getOptionId = (option: any) => {
  if (props.optionValue) {
    return option[props.optionValue];
  } else if (typeof option === 'object') {
    if (option.id) {
      return option.id;
    }
    if (option.value) {
      return option.value;
    }
  }
  return option;
};

const getOptionLabel = (option: any) => {
  if (!option) {
    return '';
  }

  if (props.optionLabel && typeof option === 'object') {
    return option[props.optionLabel];
  }

  if (props.optionLabel) {
    const fullOption = props.options?.find((listOption) => {
      return listOption[props.optionValue as string] === option;
    });
    if (fullOption) {
      return fullOption[props.optionLabel];
    } else {
      return '';
    }
  }
  return option;
};

const getOptionSubtitle = (option: any) => {
  if (!option) {
    return null;
  }

  if (props.optionSubtitle && typeof option === 'object') {
    if (props.optionSubtitleFormatter) {
      return props.optionSubtitleFormatter(option[props.optionSubtitle as string]);
    }
    return option[props.optionSubtitle as string];
  }

  if (props.optionSubtitle) {
    const fullOption = props.options?.find((listOption) => {
      return listOption[props.optionValue as string] === option;
    });
    if (fullOption) {
      if (props.optionSubtitleFormatter) {
        return props.optionSubtitleFormatter(fullOption[props.optionSubtitle as string]);
      }
      return fullOption[props.optionSubtitle as string];
    }
  }

  return null;
};

const removeOption = (option: optionType | string) => {
  if (props.multiple && model.value.length) {
    model.value = model.value.filter((opt: any) => {
      return opt !== getOptionValue(option);
    });
  }

  calculateSizePosition();
};

const selectOption = (option: optionType | string) => {
  if (props.multiple) {
    if (!model.value || model.value.length === 0) {
      model.value = [option];
    } else if (model.value.includes(option)) {
      removeOption(option);
    } else if (props.maxSelections && model.value.length === props.maxSelections) {
      // Too many options
    } else {
      model.value = [...model.value, option];
    }
  } else {
    model.value = option;
  }

  emit('optionSelected', option);
  nextTick(() => {
    calculateSizePosition();
    if (!props.multiple) {
      closeDropdown();
    }
  });
};

const selectAll = () => {
  if (allChecked.value) {
    model.value = [];
  } else if (props.options) {
    const optionVals = props.options.map((opt) => getOptionValue(opt));
    model.value = [...optionVals];
  }
};

const allChecked = computed(() => {
  return model.value?.length === props.options?.length;
});

const highlightIndex = ref(0);

const listItems = computed(() => {
  return selectOptions.value?.getElementsByTagName('li');
});

const calculateSizePosition = () => {
  const inputPos = inputSelect.value?.getBoundingClientRect();

  if (inputPos) {
    // Calculate quick-filters and footer heights dynamically
    const quickFiltersHeight = getQuickFiltersHeight();
    const footerHeight = getFooterHeight();

    const spaceAbove = inputPos.top || 0;
    const spaceBelow = window.innerHeight - inputPos.bottom;

    if (spaceAbove > spaceBelow) {
      let heightCap = spaceAbove - inputPos.height;
      if (heightCap > 280) {
        heightCap = 280 + quickFiltersHeight + footerHeight;
      }

      calculatedStyle.value = {
        bottom: -inputPos.bottom + 40 + 'px',
        maxHeight: heightCap + 'px',
        left: inputPos.left + 'px',
        width: inputPos.width + 'px',
      };

      recycleScrollerStyle.value = {
        height: heightCap - 90 - quickFiltersHeight - footerHeight + 'px',
      };
    } else {
      let heightCap = spaceBelow - inputPos.height;
      if (heightCap > 280) {
        heightCap = 280 + quickFiltersHeight + footerHeight;
      }

      calculatedStyle.value = {
        top: inputPos.top + inputPos.height + 'px',
        maxHeight: heightCap + 'px',
        left: inputPos.left + 'px',
        width: inputPos.width + 'px',
      };

      recycleScrollerStyle.value = {
        height: heightCap - 90 - quickFiltersHeight - footerHeight + 'px',
      };
    }
  }
};

const getQuickFiltersHeight = () => {
  // Try to measure the quick-filters height dynamically
  if (selectOptions.value) {
    const quickFiltersElement =
      selectOptions.value.querySelector('[data-quick-filters]') ||
      selectOptions.value.querySelector('.compare-section--quick-filters');
    if (quickFiltersElement) {
      const height = quickFiltersElement.getBoundingClientRect().height;
      // Only return the height if it's greater than 0 (quick-filters are actually present)
      return height > 0 ? height : 0;
    }
  }

  // If no quick-filters are present, return 0
  return 0;
};

const getFooterHeight = () => {
  // Try to measure the footer height dynamically
  if (selectOptions.value) {
    const footerElement = selectOptions.value.querySelector('[data-footer]');
    if (footerElement) {
      const height = footerElement.getBoundingClientRect().height;
      // Only return the height if it's greater than 0 (footer is actually present)
      return height > 0 ? height : 0;
    }
  }

  // If no footer is present, return 0
  return 0;
};

const availableOptions = computed(() => {
  if (search.value != '' && props.options) {
    const filteredOptions = [...props.options].filter((option) => {
      return getOptionLabel(option).toLocaleLowerCase().includes(search.value.toLocaleLowerCase());
    });
    return filteredOptions;
  }

  return props.options;
});

// Expose methods for parent components
defineExpose({
  toggleExpansion,
  openDropdown,
  closeDropdown,
});
</script>

<style lang="scss">
.form-input--select-dropdown .form-input--select-clear {
  color: variables.$primitives-color-red-200;
  gap: variables.$sizing-xxs;
}

.input-row--select {
  label {
    pointer-events: none;
  }

  &.input-row--expanded .form-input {
    svg {
      transform: rotate(180deg);
    }
  }

  > div {
    flex-grow: 1;
  }

  .form-input--select {
    @include mixins.flex-center($horiz: space-between);
    @include sizing.padding('xxs');
    cursor: pointer;
    min-width: 180px;
    outline: 0;

    svg {
      color: variables.$primitives-color-tertiary-300;
      margin-left: auto;
      transition: transform 0.15s cubic-bezier(1, 0.5, 0.8, 1);
    }

    .chip-colored.chip-delete button {
      z-index: 5;
    }

    .chip-colored.chip-delete svg {
      color: variables.$primitives-color-primary-400;
    }
  }

  .form-input--select-arrow {
    flex-shrink: 0;
    transition: transform 0.2s ease-in-out;
    will-change: transform;

    &.form-input--select-arrow-up {
      transform: rotate(180deg);
    }
  }
}

.form-input--select-dropdown-container {
  left: 0;
  position: relative;
  width: 240px;
  z-index: 2200;

  .form-input--select-filter {
    .input-row {
      width: 100%;
    }

    input {
      width: 100%;
    }
  }

  .form-input--select-dropdown {
    @include mixins.rounded-border();
    @include sizing.padding('xxs');
    background: variables.$primitives-color-background-100;
    box-shadow:
      0 0 #0000,
      0 0 #0000,
      0 1px 2px 0 rgba(18, 18, 23, 0.05);

    list-style-type: none;
    margin: 0;
    min-width: 240px;
    outline: 0;
    outline-color: transparent;
    overflow: auto;
    position: absolute;
    width: 100%;

    li {
      @include mixins.flex-center($horiz: flex-start);
      @include sizing.padding('xxxs');
      color: variables.$primitives-color-primary-500;
      cursor: pointer;
      overflow: hidden;
      white-space: nowrap;
    }

    li.form-input--option-active {
      background: variables.$primitives-color-primary-500;
      color: variables.$primitives-color-background-100;
    }

    li.form-input--select-option--disabled {
      opacity: 0.5;
    }

    li.form-input--select-none {
      cursor: default;
    }

    li:not(.form-input--select-none):not(.form-input--select-option--disabled):hover,
    li:focus {
      background: variables.$primitives-color-background-300;
      border-radius: variables.$sizing-xxxs;
      color: variables.$primitives-color-tertiary-500;
    }

    .form-input--select-filter .input-wrapper {
      width: 100%;
    }

    .input-row--checkbox {
      margin-right: variables.$sizing-xxxs;
    }
  }
}

.vue-recycle-scroller {
  overflow-x: auto;
  overflow-y: auto;
  position: relative;

  .vue-recycle-scroller__item-wrapper {
    width: 100%;
  }

  &.ready {
    .vue-recycle-scroller__item-view {
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
      will-change: transform;
    }

    .direction-vertical .vue-recycle-scroller__item-view {
      width: 100%;
    }
  }

  .resize-observer object {
    display: block;
    height: 100%;
    left: 0;
    overflow: hidden;
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: -1;
  }
}
.vue-recycle-scroller__slot {
  flex: auto 0 0;
}

.vue-recycle-scroller__item-wrapper {
  box-sizing: border-box;
  flex: 1;
  overflow: hidden;
  position: relative;
}

.form-input--select-option-subtitle {
  @include typography.font-body($size: 'xs', $weight: 'normal');
  color: variables.$primitives-color-tertiary-400;
}
</style>